// Text fitting engine for the TV UI kit: shrinks text until it fits its box (Tile labels/captions, FitText).
// One shared ResizeObserver and one batched pass, so N tiles cost a handful of layouts, not N × steps. Chromium 85 safe.

/** One piece of text to fit: a scale variable (`--fl-fit`) that the CSS multiplies into the font size. */
export interface FitItem {
  /** Element that gets the scale custom property (its CSS does `font-size: calc(1em * var(<prop>, 1))`). */
  host: HTMLElement;
  prop: string;
  /** Element whose computed font size / line height is the size at scale 1. */
  sizeEl: HTMLElement;
  /** Smallest font size in px. The scale never drops below max(.4, minPx / base). */
  minPx: number;
  /** Set on the fitter's root while this item sits at its minimum and still doesn't fit (CSS then allows breaking inside words). */
  minAttr: string;
  /** Does the content fit at this font size? (px = font size, lh = line height in px). Runs with line clamps switched off. */
  fits(px: number, lh: number): boolean;
}

export interface Fitter {
  root: HTMLElement;
  collect(): FitItem[];
  /** Called from the ResizeObserver with the root's new size; return false to skip refitting (e.g. only the height changed). */
  onSize?(width: number, height: number): boolean | void;
}

const STEP = 0.92;
const queue = new Set<Fitter>();
let pending = false;

/** Queue a fit. It runs before the next paint (microtask), or immediately with `sync`. */
export function scheduleFit(f: Fitter, sync = false) {
  queue.add(f);
  if (sync) flushFits();
  else if (!pending) {
    pending = true;
    queueMicrotask(() => {
      pending = false;
      flushFits();
    });
  }
}

interface Rec {
  root: HTMLElement;
  item: FitItem;
  base: number;
  ratio: number;
  scale: number;
  min: boolean;
}

function flushFits() {
  if (!queue.size) return;
  const fitters = [...queue];
  queue.clear();
  const roots: HTMLElement[] = [];
  const recs: Rec[] = [];
  // Write phase: reset every item to scale 1 and switch the line clamps off so the natural size can be measured.
  for (const f of fitters) {
    if (!f.root.isConnected) continue;
    roots.push(f.root);
    f.root.setAttribute('data-fitting', '');
    for (const item of f.collect()) {
      item.host.style.setProperty(item.prop, '1');
      recs.push({ root: f.root, item, base: 16, ratio: 1.2, scale: 1, min: false });
    }
  }
  try {
    for (const r of recs) {
      const cs = getComputedStyle(r.item.sizeEl);
      r.base = parseFloat(cs.fontSize) || 16;
      const lh = parseFloat(cs.lineHeight);
      r.ratio = lh > 0 ? lh / r.base : 1.2;
    }
    // Read, then shrink the ones that don't fit; a few rounds cover every tile at once.
    let active = recs;
    for (let round = 0; round < 14 && active.length; round++) {
      const bad = active.filter((r) => !r.item.fits(r.base * r.scale, r.base * r.scale * r.ratio));
      const next: Rec[] = [];
      for (const r of bad) {
        const minScale = Math.min(1, Math.max(0.4, r.item.minPx / r.base));
        if (r.scale <= minScale + 0.001) {
          r.min = true;
          continue;
        }
        r.scale = Math.max(minScale, r.scale * STEP);
        r.item.host.style.setProperty(r.item.prop, r.scale.toFixed(3));
        next.push(r);
      }
      active = next;
    }
  } finally {
    for (const root of roots) root.removeAttribute('data-fitting');
    for (const r of recs) {
      if (r.min) r.root.setAttribute(r.item.minAttr, '');
      else r.root.removeAttribute(r.item.minAttr);
    }
  }
}

const watched = new WeakMap<Element, Fitter>();
let observer: ResizeObserver | undefined;

function getObserver(): ResizeObserver | undefined {
  if (!observer && typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver((entries) => {
      let any = false;
      for (const e of entries) {
        const f = watched.get(e.target);
        if (!f) continue;
        const { width, height } = e.contentRect;
        if (f.onSize?.(width, height) === false) continue;
        queue.add(f);
        any = true;
      }
      // Inside the observer callback (after layout, before paint): fitting now avoids a frame with unfitted text.
      if (any) flushFits();
    });
  }
  return observer;
}

/** Fit `f` now and again whenever its root's size changes. Returns a function that detaches it. */
export function attachFitter(f: Fitter): () => void {
  const ro = getObserver();
  if (ro) {
    watched.set(f.root, f);
    ro.observe(f.root);
  } else scheduleFit(f);
  return () => {
    ro?.unobserve(f.root);
    watched.delete(f.root);
    queue.delete(f);
  };
}

const q = <T extends HTMLElement>(root: HTMLElement, sel: string) => root.querySelector<T>(sel);

/**
 * Fitter for a Tile: sets `--fl-u` (the tile's smaller side in px, drives the CSS sizes), then fits the label/sub block
 * (up to 2 label lines, then smaller font, ellipsis only at the minimum) and the focus caption (up to 2 lines).
 */
export function tileFitter(root: HTMLElement): Fitter {
  let lastW = -1;
  let lastH = -1;
  return {
    root,
    onSize(w, h) {
      if (Math.abs(w - lastW) < 0.5 && Math.abs(h - lastH) < 0.5) return false;
      lastW = w;
      lastH = h;
      root.style.setProperty('--fl-u', `${Math.round(Math.min(w, h) * 10) / 10}px`);
    },
    collect() {
      const items: FitItem[] = [];
      const face = q(root, '.fl-tile-face');
      const text = q(root, '.fl-tile-text');
      const label = q(root, '.fl-tile-label');
      const sub = q(root, '.fl-tile-sub');
      const cap = q(root, '.fl-tile-caption');
      if (face && text && label) {
        items.push({
          host: text,
          prop: '--fl-fit',
          sizeEl: label,
          minPx: 12,
          minAttr: 'data-fit-min',
          fits(_px, lh) {
            if (!text.offsetHeight) return true; // hidden in this shape (1x1)
            if (label.scrollHeight > lh * 2 + 2 || label.scrollWidth > label.clientWidth + 1) return false;
            if (sub && sub.scrollWidth > sub.clientWidth + 1) return false;
            const cs = getComputedStyle(face);
            return (
              text.offsetTop + text.offsetHeight <= face.clientHeight - (parseFloat(cs.paddingBottom) || 0) + 1.5 &&
              text.offsetLeft + text.offsetWidth <= face.clientWidth - (parseFloat(cs.paddingRight) || 0) + 1.5
            );
          },
        });
      }
      if (cap) {
        items.push({
          host: cap,
          prop: '--fl-cap',
          sizeEl: cap,
          minPx: 13,
          minAttr: 'data-cap-min',
          fits: (_px, lh) => !cap.offsetWidth || (cap.scrollWidth <= cap.clientWidth + 1 && cap.scrollHeight <= lh * 2 + 2),
        });
      }
      return items;
    },
  };
}

/** Fitter for FitText: the root itself is the text box (`lines` lines at most). */
export function textFitter(root: HTMLElement, opts: { lines: number; minPx: number }): Fitter {
  let lastW = -1;
  return {
    root,
    // Only the width matters: the height follows from the text (and changes while fitting).
    onSize(w) {
      if (Math.abs(w - lastW) < 0.5) return false;
      lastW = w;
    },
    collect: () => [
      {
        host: root,
        prop: '--fl-fit',
        sizeEl: root,
        minPx: opts.minPx,
        minAttr: 'data-fit-min',
        fits: (_px, lh) => !root.offsetWidth || (root.scrollWidth <= root.clientWidth + 1 && root.scrollHeight <= lh * opts.lines + 2),
      },
    ],
  };
}
